import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { confirmSignUp, resendSignUpCode, autoSignIn } from '@aws-amplify/auth';
import { EMAIL_LINK_OPTIONS } from './emailLinkOptions';

function Verify() {
  const location = useLocation();
  // The "Verify my email" button in the email links here with ?email=...&code=...
  const linkParams = new URLSearchParams(location.search);
  const [code, setCode] = useState((linkParams.get('code') || '').replace(/\D/g, '').slice(0, 6));
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const navigate = useNavigate();
  // Email comes from sign-up/sign-in; if someone lands here directly, they type it in
  const [email, setEmail] = useState(location.state?.email || linkParams.get('email') || '');
  const [emailInput, setEmailInput] = useState('');

  const handleVerify = async (e) => {
    e.preventDefault();
    setMessage('');
    setIsLoading(true);

    try {
      const { nextStep } = await confirmSignUp({ username: email, confirmationCode: code });
      setIsError(false);

      // Same tab they signed up in (within 3 minutes): sign them straight in
      if (nextStep.signUpStep === 'COMPLETE_AUTO_SIGN_IN') {
        setMessage('Email verified! Signing you in...');
        try {
          await autoSignIn();
          // Full reload so AuthContext picks up the new session
          window.location.href = '/';
          return;
        } catch (err) {
          console.error('Auto sign-in error:', err);
        }
      }

      setMessage('Email verified! Redirecting to sign-in...');
      setTimeout(() => navigate('/SignIn', { state: { email, verified: true } }), 2000);
    } catch (err) {
      console.error('Verification error:', err);
      if (err.name === 'NotAuthorizedException' && /CONFIRMED/.test(err.message)) {
        // e.g. clicked the email link a second time
        setMessage('Your email is already verified. Redirecting to sign-in...');
        setIsError(false);
        setTimeout(() => navigate('/SignIn', { state: { email, verified: true } }), 2000);
        return;
      }
      if (err.name === 'ExpiredCodeException') {
        setMessage('This code has expired. Tap "Resend Code" below to get a new one.');
      } else if (err.name === 'CodeMismatchException') {
        setMessage('That code doesn\'t match. Check the latest email we sent, or resend the code.');
      } else {
        setMessage(err.message || 'Verification failed');
      }
      setIsError(true);
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setIsResending(true);
    setMessage('');

    try {
      await resendSignUpCode({ username: email, options: EMAIL_LINK_OPTIONS });
      setMessage('A new verification code has been sent to your email.');
      setIsError(false);
    } catch (err) {
      console.error('Resend code error:', err);
      setMessage(err.message || 'Failed to resend code');
      setIsError(true);
    } finally {
      setIsResending(false);
    }
  };

  // No email yet: ask for it so users who left before entering their code can finish
  if (!email) {
    return (
      <div className="min-h-screen bg-white font-quicksand flex flex-col items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="text-6xl mb-6 text-center">✉️</div>
          <h1 className="text-2xl font-bold text-study-black mb-2 text-center">
            Verify Your Email
          </h1>
          <p className="text-gray-600 mb-8 text-center">
            Enter the email you signed up with. If you don't have a code, you can request a new one on the next screen.
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setEmail(emailInput.trim());
            }}
            className="space-y-5"
          >
            <input
              type="email"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              placeholder="you@example.com"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-study-black focus:outline-none focus:border-study-teal transition-all"
              required
            />
            <button
              type="submit"
              className="w-full bg-gradient-to-r from-study-teal to-study-pink text-study-black font-bold text-lg py-4 rounded-xl hover:shadow-lg transition-all"
            >
              Continue
            </button>
          </form>
          <div className="mt-8 text-center">
            <Link to="/SignUp" className="text-gray-500 hover:text-study-teal transition-all">
              ← Back to Sign Up
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white font-quicksand flex flex-col md:flex-row">
      {/* Left Side - Branding */}
      <div className="hidden md:flex md:w-1/2 bg-gradient-to-br from-study-yellow via-study-pink to-study-teal p-12 flex-col justify-center items-center text-center">
        <div className="text-8xl mb-8">✉️</div>
        <h1 className="text-4xl font-bold text-study-black mb-4">
          Check Your Email
        </h1>
        <p className="text-xl text-study-black mb-8 max-w-md">
          We've sent a verification code to your email address. Enter it below to complete your registration.
        </p>
        <div className="bg-white/30 rounded-xl p-6 max-w-sm">
          <p className="text-study-black">
            <span className="font-semibold">Sent to:</span><br />
            {email}
          </p>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex flex-col justify-center items-center p-8 md:p-12">
        <div className="w-full max-w-md">
          {/* Mobile Header */}
          <div className="md:hidden text-center mb-8">
            <div className="text-6xl mb-4">✉️</div>
            <p className="text-gray-600">
              Verification code sent to:<br />
              <span className="font-semibold text-study-black">{email}</span>
            </p>
          </div>

          <h2 className="text-3xl font-bold text-study-black mb-2 text-center md:text-left">
            Verify Your Email
          </h2>
          <p className="text-gray-600 mb-8 text-center md:text-left">
            {linkParams.get('code')
              ? 'Your code is filled in. Tap Verify Email to finish.'
              : 'Enter the 6-digit code we sent to your email'}
          </p>

          <form onSubmit={handleVerify} className="space-y-6">
            {/* Code Field */}
            <div>
              <label className="block text-sm font-semibold text-study-black mb-2">
                Verification Code
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">
                  🔑
                </span>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Enter 6-digit code"
                  className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-xl text-study-black text-center text-2xl tracking-widest focus:outline-none focus:border-study-teal transition-all"
                  required
                  disabled={isLoading}
                  maxLength={6}
                />
              </div>
            </div>

            {/* Message */}
            {message && (
              <div className={`p-4 rounded-xl text-center ${
                isError
                  ? 'bg-red-100 border border-red-300 text-red-700'
                  : 'bg-green-100 border border-green-300 text-green-700'
              }`}>
                {message}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              className="w-full bg-gradient-to-r from-study-teal to-study-pink text-study-black font-bold text-lg py-4 rounded-xl hover:shadow-lg transition-all transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              disabled={isLoading || code.length !== 6}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="animate-spin">⏳</span> Verifying...
                </span>
              ) : (
                'Verify Email'
              )}
            </button>
          </form>

          {/* Resend Code */}
          <div className="mt-8 text-center">
            <p className="text-gray-600 mb-2">
              Didn't receive the code?
            </p>
            <button
              onClick={handleResendCode}
              disabled={isResending}
              className="text-study-teal font-bold hover:text-study-pink transition-all disabled:opacity-50"
            >
              {isResending ? 'Sending...' : 'Resend Code'}
            </button>
          </div>

          {/* Back to Sign Up */}
          <div className="mt-8 pt-8 border-t border-gray-200 text-center">
            <Link
              to="/SignUp"
              className="text-gray-500 hover:text-study-teal transition-all"
            >
              ← Back to Sign Up
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Verify;
